import { useState, useEffect, useRef, useContext } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

import { Dropdown, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faEllipsisV,
  faEye,
  faTimes,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";

import { DashboardLayout } from "../../Layout/DashboardLayout";

import CustomTable from "./../../Components/CustomTable";
import { placeholderImage } from "../../Assets/images";

import "./style.css";
import { questionModal, successModal } from "../../Components/CustomModal";
import CustomFilters from "../../Components/CustomFilters";
import BASEURL from "../../Config/global";
import CustomPagination from "../../Components/CustomPagination";
import CustomLoader from "../../Components/CustomLoader";
import { PermissionsContext } from "../../Layout/PermissionContext";
import { disabledButtonByPermission } from "../../Components/Permissions";
import { deleteRequest, postRequest } from "../../Util/requests";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { listSorted } from "../../Services/Sorting";
import { getQuestionModalTitleForSorting, sortingUpdatedSuccessfullyMsg } from "../../Util/common";
import { translateEnglishToSpanish, translateFromSpanish } from "../../Util/translator";
import { SCOFA_TABLE_HEADER_KEY } from "../../Components/ScofaApp/ScofaAppData";
import ScofaAppColumn from "../../Components/ScofaApp/ScofaAppColumn";
import ReadOnlyDropdown from "../../Components/ReadOnlyDropdown";

const ArticleManagement = () => {

  const scofaColumnRef = useRef();
  const { permissions } = useContext(PermissionsContext)
  const [data, setData] = useState([]);
  const [isListPositionUpdated, setIsListPositionUpdated] = useState(false);

  const sortRef = useRef(null);

  const [enteries, setEnteries] = useState(10);
  const [offset, setOffset] = useState(0);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filterSearchValue, setFilterSearchValue] = useState("");
  const [listDropdownValue, setListDropdownValue] = useState("all");
  const [sort, setSort] = useState("all");
  const [totalCount, setTotalCount] = useState(0);
  const [swapId, setSwapId] = useState(null);
  const [swapRecordId, setSwapRecordId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState({ error: true, text: '' });
  const [loader, setLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState("english");

  useEffect(() => {
    if (currentPage != undefined) {
      setOffset(currentPage);
    }
  }, [currentPage])

  useEffect(() => {
    document.title = "Relax Scofa | Article Management";
  }, []);

  useEffect(() => {
    fetchData();
  }, [enteries, filterSearchValue, dateTo, offset, listDropdownValue, selectedLanguage]);

  const fetchData = async () => {
    try {
      const languageParam = selectedLanguage === "spanish" ? "&language=spanish" : "&language=english";
      const response = await axios.get(
        `${BASEURL}/api/articles/?limit=${enteries}&offset=${offset}&start_date=${dateFrom}&end_date=${dateTo}&search=${filterSearchValue}&featured=${listDropdownValue == 'featured' ? true : ''}${languageParam}`
      );
      if (response.data.error != true) {
        setData(response.data.data);
        setTotalCount(response.data.count);
      } else {
        console.log(response.data.message);
      }
    } catch (error) {
      console.error(error);
    }
  }

  const deleteArticle = async (id) => {
    try {
      const response = await deleteRequest(`/api/articles/${id}`);
      setData(data.filter((item) => item.id !== id));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteArticleAction = (id, name) => {
    questionModal
      .fire({
        title: `Do you want to delete ${name} Article?`,
        confirmButtonText: "Yes",
      })
      .then((result) => {
        if (result.isConfirmed) {
          deleteArticle(id);
          successModal.fire({
            text: `${name} Article has been deleted`,
            confirmButtonText: "Continue",
          });
        }
      });
  };

  const handleDuplicateArticle = async (item) => {
    // Determine target language
    const currentLanguage = item.language || 'english';
    const targetLanguage = currentLanguage === 'english' ? 'spanish' : 'english';
    const targetLanguageText = targetLanguage === 'english' ? 'English' : 'Spanish';

    questionModal
      .fire({
        title: `Do you want to duplicate this Article into ${targetLanguageText.toLowerCase()}?`,
        confirmButtonText: `Duplicate into ${targetLanguageText}`,
      })
      .then((result) => {
        if (result.isConfirmed) {
          duplicateArticle(item);
        }
      });
  };

  const duplicateArticle = async (item) => {
    setLoader(true);
    try {
      // Fetch full article data
      const response = await axios.get(`${BASEURL}/api/articles/${item.id}`);
      const articleData = response.data.data[0];

      if (!articleData) {
        setLoader(false);
        return;
      }

      // Determine current language and target language
      const currentLanguage = articleData.language || 'english';
      const targetLanguage = currentLanguage === 'english' ? 'spanish' : 'english';

      // Translate title
      let translatedTitle = articleData.title;
      if (articleData.title) {
        let translationResult;
        if (currentLanguage === 'english') {
          translationResult = await translateEnglishToSpanish(articleData.title);
        } else {
          translationResult = await translateFromSpanish(articleData.title);
        }

        if (translationResult.success) {
          translatedTitle = translationResult.translatedText;
        } else {
          setLoader(false);
          successModal.fire({
            text: `Translation failed: ${translationResult.error}`,
            confirmButtonText: "Continue",
          });
          return;
        }
      }

      // Create FormData for new article
      const formDataToSend = new FormData();


      formDataToSend.append("image", articleData.image);
      formDataToSend.append("thumbnail", articleData.thumbnail);

      formDataToSend.append("audio", articleData.audio);
      if (articleData?.video_url) {
        formDataToSend.append("video_url", articleData.video_url);
      }
      if (articleData?.video_description) {
        formDataToSend.append("video_description", articleData.video_description);
      }
      // Add other fields
      formDataToSend.append("title", translatedTitle);
      formDataToSend.append("premium", articleData.premium || false);

      // Add category
      if (articleData.subcategory_obj?.child_subcategory_id) {
        formDataToSend.append("articlecategory", articleData.subcategory_obj.child_subcategory_id);
      } else if (articleData.articlecategory) {
        formDataToSend.append("articlecategory", articleData.articlecategory);
      }

      formDataToSend.append("featured", articleData.featured || false);
      formDataToSend.append("child_content", articleData.child_content || false);
      formDataToSend.append("language", targetLanguage);
      formDataToSend.append("duration", articleData?.duration);


      // Create the duplicate article
      const createResponse = await postRequest(`/api/articles/`, formDataToSend, null);

      if (createResponse.data.error === false) {
        successModal.fire({
          text: `Article duplicated successfully in ${targetLanguage}!`,
          confirmButtonText: "Continue",
        });
        fetchData();
        setLoader(false);
      } else {
        setLoader(false);
        successModal.fire({
          text: `Failed to duplicate Article: ${createResponse.data.message || 'Unknown error'}`,
          confirmButtonText: "Continue",
        });
      }
    } catch (error) {
      console.log("error =======>   ", error);
      setLoader(false);
      successModal.fire({
        text: `An error occurred while duplicating Article: ${error.message || 'Unknown error'}`,
        confirmButtonText: "Continue",
      });
    }
  };

  const articleHeader = [
    SCOFA_TABLE_HEADER_KEY,
    {
      key: "id",
      title: "S.No",
    },
    {
      key: "title",
      title: "Title",
    },
    {
      key: "type",
      title: "Type",
    },
    {
      key: "category",
      title: "Category",
    },
    {
      key: "subcategory",
      title: "Sub Category",
    },
    {
      key: "underagecontent",
      title: "Under age content",
    },
    {
      key: "actions",
      title: "Actions",
    },
  ];

  const sortingValues = [
    {
      value: "all",
      text: "All",
    },
    {
      value: "status",
      text: "Status",
    },
    {
      value: "registered",
      text: "Registered Date",
    },
  ];

  const swipModal = (id, recordId) => {
    setError({ error: false, text: '' })
    setSwapId(id);
    // setSwapRecordId(recordId);
    setShowModal(true);
  }

  const setSorting = async () => {
    setError({ error: false, text: '' });
    let recordId = parseInt(sortRef.current.value, 10);
    if (recordId != '') {
      if (sortRef.current.value < data.length && sortRef.current.value > 0) {
        console.log(data[swapId - 1].id, data[recordId - 1].id);
        setShowModal(false);
        const formDataToSend = new FormData();
        formDataToSend.append("article_id_1", data[swapId - 1].id);
        formDataToSend.append("article_id_2", data[recordId - 1].id);
        try {
          const response = await axios.put(`${BASEURL}/api/articles/swap-articles/`, formDataToSend);
          setLoader(true);
          async function fetchData() {
            try {
              const response = await axios.get(
                `${BASEURL}/api/articles/?limit=${enteries}&offset=${offset}&start_date=${dateFrom}&end_date=${dateTo}&search=${filterSearchValue}`
              );
              if (response.data.error != true) {
                setData(response.data.data);
                setTotalCount(response.data.count);
                setLoader(false);
              } else {
                console.log(response.data.message);
              }
            } catch (error) {
              console.error(error);
            }
          }
          fetchData();
        } catch (error) {
          console.log(error);
        }
      } else {
        setError({ error: true, text: 'Please Enter Correct No' });
      }
    } else {
      setError({ error: true, text: 'Please Enter No' });
    }
  }

  const modulesWisePermission = (condition) => {
    if (!permissions) {
      return 'd-none'
    }
    return disabledButtonByPermission(permissions, condition)
  }

  const handleChangeEntries = (no) => {
    setOffset(0)
    setEnteries(no)
  }

  const onSortEnd = (result) => {
    if (!result.destination) return;

    const oldIndex = result.source.index;
    const newIndex = result.destination.index;

    if (oldIndex === newIndex) return;

    const reorderedList = Array.from(data);
    const [movedItem] = reorderedList.splice(oldIndex, 1);
    reorderedList.splice(newIndex, 0, movedItem);
    setIsListPositionUpdated(true);
    setData(reorderedList)

  };

  const updateOrdering = async () => {

    try {
      questionModal
        .fire({
          title: getQuestionModalTitleForSorting(listDropdownValue, 'Article'),
          confirmButtonText: "Yes",
        })
        .then((result) => {
          if (result.isConfirmed) {
            setLoader(true);
            callSortingApi()
          }
        });
    } catch (error) {
      console.log(error)
    }

  }

  const callSortingApi = async () => {
    try {
      const url = listDropdownValue === 'all' ? '/api/articles/update-sequence/' : `/api/articles/update-featured-sequence/`;
      const result = await listSorted({
        url: url,
        data: data,
        key: 'articleid'
      })
      if (!result.data.error) {
        setLoader(false);
        successModal.fire({
          text: sortingUpdatedSuccessfullyMsg(listDropdownValue, 'Article'),
          confirmButtonText: "Continue",
        });
        setIsListPositionUpdated(false);
      } else {
        setLoader(false);
      }
    } catch (error) {
      setLoader(false);
    }
  }


  return (
    <>
      <DashboardLayout>
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-lg-4 mb-2">
              <h2 className="mainTitle">Article</h2>
            </div>
            <div className="col-lg-8 text-end mb-2">
              <button onClick={() => scofaColumnRef.current.handleScofaFlag()} className="customButton primaryButton me-3">
                Update Scofa Tracks
              </button>
              <button disabled={data.length > 0 && isListPositionUpdated ? false : true} className="customButton primaryButton me-3" onClick={updateOrdering}>
                Update Ordering
              </button>
              <Link
                to={"/article-management/add-article"}
                className={`customButton primaryButton ${modulesWisePermission('can_add')}`}
              >
                Add Article
              </Link>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-12">
              <CustomFilters
                enteries={enteries}
                totalCount={totalCount}
                entriesFilter={true}
                setEnteries={handleChangeEntries}
                filterSearch={true}
                filterSearchValue={filterSearchValue}
                setFilterSearchValue={setFilterSearchValue}
                listDropdown={true}
                listDropdownValue={listDropdownValue}
                setListDropdownValue={setListDropdownValue}
                setDateFrom={setDateFrom}
                setDateTo={setDateTo}
                languageFilter
                languageValue={selectedLanguage}
                setLanguageValue={setSelectedLanguage}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-12">
              <DragDropContext onDragEnd={onSortEnd}>
                <Droppable droppableId="soundMix-list">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      <CustomTable headers={articleHeader}>
                        <tbody>
                          {data && data.map((item, index) => (
                            <Draggable
                              draggableId={`mix-${item.id}`}
                              index={index}
                              key={item.id}
                            >
                              {(provided, snapshot) => (
                                <tr ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`my-1 ${snapshot.isDragging ? "dragging" : ""}`}
                                >
                                  <ScofaAppColumn ref={scofaColumnRef} item={item} category="articles" />
                                  <td>
                                    {/* <button type="button" className="btn lightColor outline-0 border-0" onClick={() => swipModal(index + 1, item.id)}>{index + 1}</button> */}
                                    {index + 1}
                                  </td>
                                  <td className="text-capitalize">
                                    <img
                                      src={
                                        item.thumbnail
                                          ? `${item.thumbnail}`
                                          : placeholderImage
                                      }
                                      alt="thumbnail"
                                      className="thumbnail"
                                    />
                                    {item.title}
                                  </td>
                                  <td>{item.premium ? "Premium" : "Free"}</td>
                                  <td><ReadOnlyDropdown options={item?.categories} /></td>
                                  <td><ReadOnlyDropdown options={item?.subCategories} /></td>
                                  <td>{item?.child_content ? "YES" : "NO"}</td>
                                  <td>
                                    <Dropdown className="tableDropdown">
                                      <Dropdown.Toggle
                                        variant="transparent"
                                        className="notButton classicToggle"
                                      >
                                        <FontAwesomeIcon icon={faEllipsisV} />
                                      </Dropdown.Toggle>
                                      <Dropdown.Menu
                                        align="end"
                                        className="tableDropdownMenu"
                                      >
                                        <Link
                                          to={`/article-management/article-details/${item.id}`}
                                          className={`tableAction ${modulesWisePermission('can_view')}`}
                                        >
                                          <FontAwesomeIcon
                                            icon={faEye}
                                            className="tableActionIcon"
                                          />
                                          View
                                        </Link>
                                        <Link
                                          to={`/article-management/edit-article/${item.id}`}
                                          className={`tableAction ${modulesWisePermission('can_edit')}`}
                                        >
                                          <FontAwesomeIcon
                                            icon={faEdit}
                                            className="tableActionIcon"
                                          />
                                          Edit
                                        </Link>
                                        <button
                                          className={`tableAction ${modulesWisePermission('can_add')}`}
                                          onClick={() => {
                                            handleDuplicateArticle(item);
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={faCopy}
                                            className="tableActionIcon"
                                          />
                                          Duplicate
                                        </button>
                                        <button
                                          className={`tableAction ${modulesWisePermission('can_delete')}`}
                                          onClick={() => {
                                            deleteArticleAction(item.id, item.title);
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={faTimes}
                                            className="tableActionIcon"
                                          />
                                          Delete
                                        </button>
                                      </Dropdown.Menu>
                                    </Dropdown>
                                  </td>
                                </tr>
                              )}
                            </Draggable>

                          ))}
                          {provided.placeholder}
                        </tbody>
                      </CustomTable>
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-12">
              <CustomPagination
                enteries={enteries}
                totalCount={totalCount}
                setCurrentPage={setOffset}
                currentPage={offset}
              />
            </div>
          </div>
        </div>
      </DashboardLayout>

      {loader && <CustomLoader />}

      <Modal
        show={showModal}
        centered
        className="swap-modal"
        backdrop="static"
      >
        <Modal.Body>
          <div className="pb-5 w-100 d-flex flex-column align-items-center">
            <div className="text-end ms-auto">
              <button
                className="closeButton notButton ms-auto"
                onClick={() => { setShowModal(false); }}
              >
                <FontAwesomeIcon
                  icon={faTimes}
                  className="cross-icon"
                  color="white"
                />
              </button>
            </div>
            <div className="w-100 d-flex flex-column align-items-center" style={{ maxWidth: '500px' }}>
              <div className="d-flex align-items-center mt-3 pt-3">
                <h2 className="mb-0 me-4">Record <b className="mx-2">{swapId}</b> swap with </h2>
                <input type="number" ref={sortRef} onChange={() => setError({ error: false, text: '' })} />
              </div>
              {error?.error && <small className="text-danger mt-3">{error.text}</small>}
            </div>
            <div className="mt-3 pt-3 d-flex gap-3 justify-content-center">
              <button style={{ backgroundColor: '#ffffff1f' }} className='px-5 py-2 fs-6 fw-light text-white text-capitalize btn rounded-pill d-flex align-items-center justify-content-center flex-shrink-0' onClick={setSorting}>
                <span className=''>Update</span>
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>


    </>
  );
};
export default ArticleManagement;
